import { createFileRoute } from '@tanstack/react-router';
import { fallback, zodValidator } from '@tanstack/zod-adapter';
import z from 'zod';

import { BreadcrumbItemProps } from '@/features/admin/components/AdminPageHeader';
import { getOrganizationsDefaultParams } from '@/features/manage-orgs/actions/getOrganizations';
import { OrgsListRoot } from '@/features/manage-orgs/components/OrgsListRoot';
import { getOrganizationsQueryOptions } from '@/features/manage-orgs/queries/getOrganizations';
import { AppPageContent } from '@/features/shared/components/AppPageContent';
import { AppPageHeader } from '@/features/shared/components/AppPageHeader';
import { UserRoles } from '@/lib/constants/auth.constants.ts';
import { requireRole } from '@/lib/validation';

const usersSearchParams = z.object({
  search: fallback(z.string(), '').default(''),
  page: fallback(z.number(), 0).default(0),
  fields: fallback(z.string(), getOrganizationsDefaultParams.fields).default(
    getOrganizationsDefaultParams.fields
  ),
  perPage: fallback(z.number(), getOrganizationsDefaultParams.limit).default(
    getOrganizationsDefaultParams.limit
  ),
  sort: fallback(z.string(), getOrganizationsDefaultParams.sort).default(
    getOrganizationsDefaultParams.sort
  ),
  orderBy: fallback(z.string(), getOrganizationsDefaultParams.orderBy).default(
    getOrganizationsDefaultParams.orderBy
  ),
  organizationId: fallback(z.string(), '').default(''),
  role: fallback(z.string(), '').default(''),
});

export const Route = createFileRoute('/_app/app/admin-section/orgs/')({
  component: RouteComponent,
  beforeLoad: requireRole([UserRoles.ADMINISTRATOR]),
  validateSearch: zodValidator(usersSearchParams),
  loaderDeps: ({ search }) => ({
    search,
  }),
  loader: async ({ context, deps }) => {
    const { search } = deps;
    await context.queryClient.prefetchQuery;
    getOrganizationsQueryOptions({
      offset: search.page * search.perPage,
      limit: search.perPage,
      sort: search.sort,
      orderBy: search.orderBy,
      search: search.search,
    });
  },
  ssr: false,
});

const breadcrumbItems: BreadcrumbItemProps[] = [
  {
    label: 'Home',
    link: { to: '/app' },
  },
  {
    label: 'Administration',
    link: { to: '/app' },
  },
  {
    label: 'Organizations',
    link: { to: '/app/admin-section/orgs' },
  },
];

function RouteComponent() {
  return (
    <AppPageContent className="overflow-hidden h-[calc(100%-16px)] flex flex-col">
      <AppPageHeader breadcrumbItems={breadcrumbItems} />
      <OrgsListRoot />
    </AppPageContent>
  );
}
